/**
 * Multidisciplinary review block — 4-corner per-player notes.
 *
 * Web1's MultidisciplinaryReviewEditor was a templated multi-column
 * form with AI auto-fill, voice-per-column, and a `player_reviews`
 * schema. Web2 ships a leaner first cut: 4 corner textareas backed by
 * localStorage. The shape lines up with the eventual server-side row
 * (corner_id keyed string) so we can swap to Supabase persistence
 * without touching the UI.
 *
 * Future: when the schema migration lands, replace `loadDossier` /
 * `saveDossier` with calls to a `player_dossiers` row keyed by
 * (player_id, coach_id, period_start). For now the coach's notes
 * persist on-device only.
 */

export type DossierCorner = 'technical_tactical' | 'physical' | 'psychological' | 'social';

export interface DossierNotes {
  technical_tactical: string;
  physical: string;
  psychological: string;
  social: string;
  /** When the notes were last updated, ISO 8601. */
  updated_at: string;
}

const STORAGE_KEY = '4cm_player_dossier';

const EMPTY: DossierNotes = {
  technical_tactical: '',
  physical: '',
  psychological: '',
  social: '',
  updated_at: '',
};

function readMap(): Record<string, DossierNotes> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, DossierNotes>)
      : {};
  } catch {
    return {};
  }
}

function writeMap(map: Record<string, DossierNotes>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* swallow — quota / privacy */
  }
}

export function loadDossier(playerId: string): DossierNotes {
  const map = readMap();
  return map[playerId] ?? EMPTY;
}

export function saveDossier(playerId: string, patch: Partial<DossierNotes>): DossierNotes {
  const map = readMap();
  const prev = map[playerId] ?? EMPTY;
  const next: DossierNotes = {
    ...prev,
    ...patch,
    updated_at: new Date().toISOString(),
  };
  map[playerId] = next;
  writeMap(map);
  return next;
}

export function dossierHasContent(notes: DossierNotes): boolean {
  return (
    notes.technical_tactical.trim().length > 0 ||
    notes.physical.trim().length > 0 ||
    notes.psychological.trim().length > 0 ||
    notes.social.trim().length > 0
  );
}

export function dossierCompletePct(notes: DossierNotes): number {
  const filled = [
    notes.technical_tactical,
    notes.physical,
    notes.psychological,
    notes.social,
  ].filter((s) => s.trim().length >= 10).length;
  return Math.round((filled / 4) * 100);
}
